import { Component, inject, Input, PipeTransform } from '@angular/core'
import { FilesizePipe } from '../../filesize.pipe'
import { formatTimeUntil } from '../../utils'
import { MatPaginatorModule } from '@angular/material/paginator'
import { MatTableModule } from '@angular/material/table'
import { TranslateModule } from '@ngx-translate/core'
import { MatSortModule } from '@angular/material/sort'
import { RouterLink } from '@angular/router'
import { AsyncPipe, DatePipe, JsonPipe } from '@angular/common'
import { AdminBooleanValueComponent } from '../../admin/admin-boolean-value/admin-boolean-value.component'
import { DurationPipe } from '../../duration.pipe'
import { MatMenuModule } from '@angular/material/menu'
import { FormsModule } from '@angular/forms'
import { BritishDatePipe } from '../../british-date.pipe'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { AvatarComponent } from '../../avatar/avatar.component'
import { MatChip } from '@angular/material/chips'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { UsersService } from '../../users.service'

export type ColumnType =
  | 'TEXT'
  | 'DATE'
  | 'DURATION'
  | 'CALL_STATUS'
  | 'COMPLETED_CALL_STATUS'
  | 'BOOLEAN'
  | 'USER'
  | 'USER_AVATAR_LIST'
  | 'CALL'
  | 'ORGANIZATION'
  | 'CALL_USERS'
  | 'JSON'
  | 'LIST'
  | 'NUMBER'
  | 'NUMBER_WITH_ONE_DECIMAL'
  | 'MEETING_PROVIDER'
  | 'UPLOADED_DOCUMENTS'
  | 'STARTS_IN'
  | 'CALL_TYPE'
  | 'USER_ROLE'
  | 'TIME_RANGE'

@Component({
  selector: 'app-paginated-table-cell',
  standalone: true,
  imports: [
    MatTableModule,
    MatPaginatorModule,
    TranslateModule,
    MatSortModule,
    RouterLink,
    FormsModule,
    AdminBooleanValueComponent,
    DurationPipe,
    JsonPipe,
    MatMenuModule,
    MatIcon,
    MatTooltip,
    AvatarComponent,
    MatChip,
    MatProgressBarModule,
    AsyncPipe,
  ],
  providers: [DatePipe, BritishDatePipe],
  templateUrl: './paginated-table-cell.component.html',
  styleUrl: './paginated-table-cell.component.scss',
})
export class PaginatedTableCellComponent {
  @Input({ required: true })
  data!: any

  @Input({ required: true })
  columnType!: ColumnType

  @Input({ required: true })
  timezone!: 'local' | 'british'

  defaultDatePipe = inject(DatePipe)
  britishDatePipe = inject(BritishDatePipe)
  usersService = inject(UsersService)
  currentUser$ = this.usersService.currentUser$
  private fileSizePipe = new FilesizePipe()

  get datePipe(): PipeTransform {
    return this.timezone === 'local' ? this.defaultDatePipe : this.britishDatePipe
  }

  isValidNumber(data: any) {
    return typeof data === 'number' && !isNaN(data)
  }

  formatFileSize(bytes: number): string {
    return this.fileSizePipe.transform(bytes) as string
  }

  getTimingState(
    data: {
      date: Date | string | null
      endDate: Date | string | null
    } | null
  ): { type: 'upcoming'; text: string } | { type: 'ongoing'; progress: number } | null {
    if (data === null || data.date === null) {
      return null
    }

    const startDate = typeof data.date === 'string' ? new Date(data.date) : data.date
    const endDate = data.endDate !== null ? (typeof data.endDate === 'string' ? new Date(data.endDate) : data.endDate) : null

    const upcomingText = formatTimeUntil(startDate)
    if (upcomingText !== null) {
      return { type: 'upcoming', text: upcomingText }
    }

    const now = new Date()
    if (endDate !== null && endDate.getTime() > now.getTime()) {
      const totalDuration = endDate.getTime() - startDate.getTime()
      const elapsed = now.getTime() - startDate.getTime()
      const progress = Math.min(Math.round((elapsed / totalDuration) * 100), 100)
      return { type: 'ongoing', progress }
    }

    return null
  }

  getOrganizationColor(organizationId: number): string {
    const hue = (organizationId * 137.508) % 360
    return `hsl(${hue}, 70%, 90%)`
  }
}
